import { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  bankCommonInfoSchema,
  imageWithUploadSchema,
  type BankName,
  type ImageWithUpload,
  type OrganizationProfile,
  type OrgProfileUpdate,
} from '@hero/schema';
import { useOrgProfileUpdateApi } from '@hero/api-hooks';
import { CheckIcon } from '@hero/web-icons';
import { Button } from '../../../ui/Button';
import { Spinner } from '../../../app/Spinner';
import { BankNameField } from '../../../app/OrgProfileFields/BankNameField';
import { IbanField } from '../../../app/OrgProfileFields/IbanField';
import { AccountHolderNameField } from '../../../app/OrgProfileFields/AccountHolderNameField';
import { AccountProofImageField } from '../../../app/OrgProfileFields/AccountProofImageField';
import { makeChipFor, type OrgProfileSectionContext } from './types';

/**
 * Bank-section resolver. Mirrors `bankCommonInfoSchema.partial()`
 * so the client doesn't require any field individually — server-
 * side, `OrgProfileService.applyBankSection` enforces "all four
 * fields when creating a bank, any subset when updating an
 * existing one". The form-level mode lets the user save any
 * single dirty field once a bank exists.
 *
 * Relaxing `accountImage` to `imageWithUploadSchema` keeps the
 * fresh-upload `{ upload }` shape valid in form state.
 */
const bankSchema = bankCommonInfoSchema.partial().extend({
  accountImage: imageWithUploadSchema.optional(),
});

type BankFormValues = z.infer<typeof bankSchema>;

export interface CarrierBankingFormProps {
  profile: Partial<OrganizationProfile> | undefined;
  /**
   * Financing context surfaces every bank field as `required`
   * (lender deposits payouts to this account). On the profile
   * tab we show no chips because banking is genuinely optional
   * for non-financing carriers.
   */
  displayFor?: OrgProfileSectionContext;
  hideSubmit?: boolean;
  disabled?: boolean;
  onSaved?: (patch: { bank: NonNullable<OrgProfileUpdate['bank']> }) => void;
}

/**
 * `CarrierBankingForm` — Banking tab. Edits the four bank fields
 * that lenders use to route payouts to the carrier (bank, IBAN,
 * account holder, proof image).
 *
 * Backend integration:
 *  - Submits `{ bank: { ... } }` via `useOrgProfileUpdateApi`.
 *  - The service writes against the carrier's *primary* Bank doc.
 *    If no bank exists, the server expects all four create fields
 *    in one go; the form's "Save" only enables once every field is
 *    populated when the user is creating the bank for the first
 *    time. When the bank already exists, single-field patches
 *    work and only the dirty fields end up in the body.
 */
export function CarrierBankingForm({
  profile,
  displayFor = 'profile',
  hideSubmit,
  disabled,
  onSaved,
}: CarrierBankingFormProps) {
  const { t } = useTranslation('m-organization');
  const update = useOrgProfileUpdateApi();
  const chip = makeChipFor(displayFor);

  const defaultValues = useMemo<BankFormValues>(
    () => ({
      bankName: profile?.bankName as BankName | undefined,
      IBAN: profile?.bankIBAN,
      accountName: profile?.bankAccountName,
      accountImage: profile?.bankAccountImage as ImageWithUpload | undefined,
    }),
    [
      profile?._id,
      profile?.bankName,
      profile?.bankIBAN,
      profile?.bankAccountName,
    ],
  );

  const methods = useForm<BankFormValues>({
    resolver: zodResolver(bankSchema),
    defaultValues,
    mode: 'onSubmit',
  });

  useEffect(() => {
    methods.reset(defaultValues);
  }, [defaultValues, methods]);

  const onSubmit = methods.handleSubmit(async values => {
    const dirty = methods.formState.dirtyFields;
    const bank: NonNullable<OrgProfileUpdate['bank']> = {};
    if (dirty.bankName) bank.bankName = values.bankName;
    if (dirty.IBAN) bank.IBAN = values.IBAN;
    if (dirty.accountName) bank.accountName = values.accountName;
    if (dirty.accountImage)
      bank.accountImage = values.accountImage as NonNullable<
        OrgProfileUpdate['bank']
      >['accountImage'];
    if (Object.keys(bank).length === 0) return;
    try {
      await update.mutateAsync({ bank });
      onSaved?.({ bank });
    } catch (err) {
      // The "missing fields for new bank" error comes back with a
      // helpful list inside the i18n message — the global mutation
      // toast surfaces it. We just keep the form open here.
      console.error('Failed to save carrier banking:', err);
    }
  });

  return (
    <FormProvider {...methods}>
      <form onSubmit={onSubmit} className="space-y-6" noValidate>
        <BankNameField name="bankName" disabled={disabled} financing={chip('hard')} />
        <IbanField name="IBAN" disabled={disabled} financing={chip('hard')} />
        <AccountHolderNameField
          name="accountName"
          disabled={disabled}
          financing={chip('hard')}
        />
        <AccountProofImageField
          name="accountImage"
          disabled={disabled}
          financing={chip('hard')}
        />

        {!hideSubmit ? (
          <div className="flex items-center justify-end">
            <Button type="submit" disabled={disabled || update.isPending}>
              {update.isPending ? (
                <Spinner />
              ) : (
                <span className="flex items-center gap-2">
                  <CheckIcon className="size-4" />
                  {t('buttons.save')}
                </span>
              )}
            </Button>
          </div>
        ) : null}
      </form>
    </FormProvider>
  );
}
